/**
 * The map's toolbar: the canvas / table switch; while editing, the add menu,
 * auto layout (top to bottom or left to right) and delete; on the canvas, the
 * camera buttons; undo / redo when history is on; and the host's own content
 * at the end (e.g. the CRM's save status). A read-only map gets the switch and
 * the camera buttons only.
 *
 * Every button has a hover title and an accessible name from FlowMapLabels.
 * Text labels fold away on a narrow map (liveCss.ts) but stay readable to
 * screen readers.
 */
import type { ReactElement, ReactNode } from "react";
import { AddMenu } from "./AddMenu.js";
import {
  IconArrowDown,
  IconArrowRight,
  IconCanvas,
  IconFit,
  IconLayout,
  IconRedo,
  IconTable,
  IconTrash,
  IconUndo,
  IconZoomIn,
  IconZoomOut,
} from "./icons.js";
import type { FlowDirection, FlowMapLabels, FlowNodeKind, FlowView } from "./model.js";

export interface ToolbarHistory {
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
}

export interface ToolbarProps {
  labels: FlowMapLabels;
  view: FlowView;
  showSwitch: boolean;
  onView: (view: FlowView) => void;
  editable: boolean;
  /** The live canvas is up (the camera buttons work). */
  canvasReady: boolean;
  onAdd: (kind: FlowNodeKind) => void;
  canAdd: boolean;
  onArrange: (direction: FlowDirection) => void;
  canArrange: boolean;
  onDelete: () => void;
  canDelete: boolean;
  onFit: () => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  /** null: no undo / redo buttons. */
  history: ToolbarHistory | null;
  extra?: ReactNode;
}

function IconAction({
  label,
  title,
  onClick,
  disabled,
  children,
}: {
  label: string;
  title?: string;
  onClick: () => void;
  disabled?: boolean;
  children: ReactNode;
}): ReactElement {
  return (
    <button type="button" className="wa-fm__btn wa-fm__btn--icon" aria-label={label} title={title ?? label} onClick={onClick} disabled={disabled}>
      {children}
    </button>
  );
}

export function Toolbar(p: ToolbarProps): ReactElement | null {
  const { labels } = p;
  const onCanvas = p.view === "canvas";
  const parts: ReactNode[] = [];

  if (p.showSwitch) {
    parts.push(
      <div key="switch" className="wa-fm__switch" role="group" aria-label={labels.view}>
        <button
          type="button"
          className="wa-fm__switch-opt wa-fm__switch-opt--canvas"
          aria-pressed={onCanvas}
          title={labels.canvasView}
          onClick={() => p.onView("canvas")}
        >
          <IconCanvas size={16} />
          <span className="wa-fm__switch-text">{labels.canvasView}</span>
        </button>
        <button
          type="button"
          className="wa-fm__switch-opt wa-fm__switch-opt--table"
          aria-pressed={!onCanvas}
          title={labels.tableView}
          onClick={() => p.onView("table")}
        >
          <IconTable size={16} />
          <span className="wa-fm__switch-text">{labels.tableView}</span>
        </button>
      </div>,
    );
  }

  if (p.editable) {
    parts.push(
      <div key="edit" className="wa-fm__bar-group">
        <AddMenu labels={labels} onAdd={p.onAdd} disabled={!p.canAdd} />
        <div className="wa-fm__seg" role="group" aria-label={labels.autoLayout} title={labels.autoLayout}>
          <IconLayout size={15} />
          <IconAction
            label={labels.directionTB}
            title={`${labels.autoLayout}: ${labels.directionTB}`}
            onClick={() => p.onArrange("TB")}
            disabled={!p.canArrange}
          >
            <IconArrowDown size={15} />
          </IconAction>
          <IconAction
            label={labels.directionLR}
            title={`${labels.autoLayout}: ${labels.directionLR}`}
            onClick={() => p.onArrange("LR")}
            disabled={!p.canArrange}
          >
            <IconArrowRight size={15} />
          </IconAction>
        </div>
        <IconAction label={labels.deleteSelected} onClick={p.onDelete} disabled={!p.canDelete}>
          <IconTrash size={16} />
        </IconAction>
      </div>,
    );
  }

  if (onCanvas) {
    parts.push(
      <div key="camera" className="wa-fm__bar-group">
        <IconAction label={labels.fitView} onClick={p.onFit} disabled={!p.canvasReady}>
          <IconFit size={16} />
        </IconAction>
        <IconAction label={labels.zoomOut} onClick={p.onZoomOut} disabled={!p.canvasReady}>
          <IconZoomOut size={16} />
        </IconAction>
        <IconAction label={labels.zoomIn} onClick={p.onZoomIn} disabled={!p.canvasReady}>
          <IconZoomIn size={16} />
        </IconAction>
      </div>,
    );
  }

  if (p.history) {
    const h = p.history;
    parts.push(
      <div key="history" className="wa-fm__bar-group">
        <IconAction label={labels.undo} onClick={h.onUndo} disabled={!h.canUndo}>
          <IconUndo size={16} />
        </IconAction>
        <IconAction label={labels.redo} onClick={h.onRedo} disabled={!h.canRedo}>
          <IconRedo size={16} />
        </IconAction>
      </div>,
    );
  }

  if (p.extra != null && p.extra !== false) {
    parts.push(
      <div key="spacer" className="wa-fm__spacer" />,
      <div key="extra" className="wa-fm__bar-group">
        {p.extra}
      </div>,
    );
  }

  return parts.length ? <div className="wa-fm__bar">{parts}</div> : null;
}
